import { useState } from 'react';
import { ExpandMore } from '@material-ui/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Box
} from '@material-ui/core';
import { TitleDivider } from '../../../components/titleDivider';
import { brokerTypeOptions, createRadioField } from '@roc/ui';
import { observer } from 'mobx-react';
import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { yesNoOptionsBoolean } from '@roc/feature-utils';
import { PercentagePicker } from '../../leverage/components/leverageComponents';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { Switch } from '@material-ui/core';
import { makeStyles, Typography } from '@material-ui/core';
import { CurrencyField } from '@roc/ui';
import { useEffect } from 'react';


const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  activeLabel: {
    color: theme.palette.primary.main,
  },
  inactiveLabel: {
    color: theme.palette.text.secondary,
  },
  noBorders: {
    border: 'none',
  },
  disabled: {
    background: 'transparent'
  },
  feeLabel: {
    fontSize: '14px',
    color: '#939494',
  },
  CurrencyField: {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      fontSize: '20px!important',
      fontWeight: 'bold !important',
      color: false,
    },
    '& .MuiTypography-colorTextSecondary': {
      color: false,
      fontWeight: 'bold',
    },
  },
}));

interface BrokerAccordionProps {
  store: QuoteTypeStore;
}

export const BrokerAccordion = observer(
  ({ store }: BrokerAccordionProps) => {
    const { brokerStore, loanEconomicsStore } = store;
    const { globalStore } = useBaseStore();
    const { userStore } = useUserStore();
    const [expanded, setExpanded] = useState(true);
    const classes = useStyles();
    const hasBroker =
      brokerStore.brokerFormStore.form.fields.hasBroker.value === true;
    const totalLoanAmount = loanEconomicsStore.totalLoanAmount;
    const displayBrokerFee = brokerStore.brokerFormStore?.form?.fields?.displayBrokerFee?.value;

    useEffect(() => {
      if (expanded) {
        brokerStore.initializeBrokerFee(totalLoanAmount);
      }
    }, [expanded, totalLoanAmount, brokerStore]);

    useEffect(() => {
      if (globalStore.lenderInfo?.isInternal !== true) {
        brokerStore.brokerFormStore.onFieldChange('brokerType', 'ON_THE_HUD');
      }
    }, [globalStore.lenderInfo?.isInternal, brokerStore]);


    const handleBrokerFeeChangeAmount = (value) => {
      brokerStore.handleBrokerFeeChange(value, totalLoanAmount);
    };

    const handleBrokerFeeChangePoints = (value) => {
      brokerStore.handleBrokerFeeChange(value, totalLoanAmount);
    };

    const handleToggle = () => {
      brokerStore.handleDisplayToggle(totalLoanAmount);
    };



    return (
      <Accordion
        expanded={expanded}
        onChange={(e, expanded) => setExpanded(expanded)}
        elevation={0}
      >
        <AccordionSummary
          expandIcon={<ExpandMore fontSize="large" />}
          aria-controls="broker-accordion"
        >
          <TitleDivider title="Broker" />
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} sm={6}>
              {createRadioField({
                label: 'Is there a broker involved in this loan?',
                fieldName: 'hasBroker',
                testId: 'hasBroker',
                options: yesNoOptionsBoolean,
                store: brokerStore.brokerFormStore,
                row: true,
                standaloneLabel: false,
              })}
            </Grid>
            {hasBroker && (userStore.allowLoanPricing || userStore.isLenderOriginator) && (
              <>
                {globalStore?.lenderInfo?.isInternal === true && (
                  <Grid item xs={12} md={4} sm={6}>
                    {createRadioField({
                      label: 'How do you want the broker to be paid?',
                      fieldName: 'brokerType',
                      testId: 'brokerType',
                      store: brokerStore.brokerFormStore,
                      options: brokerTypeOptions,
                      row: true,
                      standaloneLabel: false,
                    })}
                  </Grid>
                )}
                <Grid item xs={12} md={4} sm={6}>
                  {displayBrokerFee &&
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="body2" className={classes.feeLabel}>
                        Broker Fees
                      </Typography>
                      <CurrencyField
                        value={brokerStore.brokerFormStore.form.fields.brokerAmount.value || 0}
                        variant="standard"
                        fullWidth={false}
                        testId="brokerFee"
                        allowNegative={false}
                        decimalScale={2}
                        inputClassname={classes.CurrencyField}
                        onChange={(e, value) => handleBrokerFeeChangePoints(value)}
                      />
                    </Box>
                  }
                  {!displayBrokerFee &&
                    <PercentagePicker
                      fieldName={'brokerPoints'}
                      label="Broker Points *"
                      value={
                        brokerStore.brokerFormStore.form.fields.brokerPoints.value
                      }
                      min={0}
                      max={100}
                      onChange={(value: number) => handleBrokerFeeChangeAmount(value)}
                    />
                  }
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <Grid container alignItems="center" justifyContent="flex-start">
                    <Grid item>
                      <Typography
                        className={`${classes.label} ${displayBrokerFee ? classes.activeLabel : classes.inactiveLabel
                          }`}
                      >
                        %
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Switch
                        checked={displayBrokerFee}
                        onChange={() => handleToggle()}
                        color="primary"
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography
                        className={`${classes.label} ${displayBrokerFee ? classes.activeLabel : classes.inactiveLabel
                          }`}
                      >
                        $
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  }
);
